

import React, { useState, useEffect } from 'react';
import { Modal } from "react-bootstrap";
import { useRecoilState } from "recoil";
import { useConnect, useDisconnect, useWalletClient, useChainId, useSwitchChain, useSignMessage, useAccount } from "wagmi";
import { signMessage } from '@wagmi/core';
import { createWalletClient, custom, createPublicClient, http, encodeFunctionData } from 'viem';
import { arbitrum, alchemy } from "@account-kit/infra";
import { WalletClientSigner } from '@aa-sdk/core';
import { createSmartWalletClient, signPreparedCalls } from "@account-kit/wallet-client";
import axios from "axios";
import Loader from "../../hooks/loader";
import BalanceOfAbi from "../../utils/BalanceOfAbi.json";
import {
    userSmartAccount,
    userSmartAccountClient,
    userPublicClient,
    userProfileData
} from "../RecoilAtom";
import {
    platform_chainId,
    ALCHEMY_API_KEY,
    PAYMASTER_POLICY_ID,
    api_url,
    usdr_token,
    RAIN_ARBI_CONTRACT_ADDRESS,
    OLD_FACTORY_CONTRACT,
} from "../../utils/Enviroment";
import { getTxHashAndReceipt, getWalletImage } from "../../utils/helpers";
import { getUsersProfile } from "../../utils/services";
import getNodeUrl from '../../utils/getRpcUrl';
import { notifyError, notifySuccess, notifyWarning } from "../Toast";
import { LOGIN_SUCCESS, SAFE_FUNDS_WARNING } from '../../utils/ConstantMessages';
import { useWalletList } from '../../hooks/useWalletList';
import { useTranslation } from 'react-i18next';
import { config } from '../../config/wagmi';
import { useTheme } from '../../contexts/ThemeProvider';
import { DARK_THEME } from '../../utils/constants';
import { useGetAllBalances } from '../AllBalances';
import { useLocation } from "react-router-dom";
import { useSessionPermissions } from '../../hooks/checkSessionPermissions';
import { useCheckAllowance } from '../../hooks/dataSender/Allowance';
import { useSwipeClose } from '../../hooks/useSwipeClose';



const isMobile = () => /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

const MOBILE_WC_FALLBACK_WALLETS = ['MetaMask', 'Binance Wallet'];

const getWalletDeepLink = (walletName, wcUri) => {
    const encoded = encodeURIComponent(wcUri);
    switch (walletName) {
        case 'MetaMask':
            return `metamask://wc?uri=${encoded}`;
        case 'Binance Wallet':
            return `bnc://app.binance.com/cedefi/wc?uri=${encoded}`;
        default:
            return null;
    }
};

const getWalletOpenLink = (walletName) => {
    switch (walletName) {
        case 'MetaMask':
            return 'metamask://';
        case 'Binance Wallet':
            return 'bnc://app.binance.com/cedefi';
        default:
            return null;
    }
};

const getWalletInstallLink = (walletName) => {
    switch (walletName) {
        case 'Binance Wallet':
            return 'https://www.binance.com/en/download';
        default:
            return null;
    }
};

const BINANCE_DOWNLOAD_URL = 'https://www.binance.com/en/download';

const handleWalletNotInstalled = (walletName) => {
    if (walletName === 'Binance Wallet') {
        window.open(BINANCE_DOWNLOAD_URL, '_blank');
        return;
    }
    notifyWarning(`Please install ${walletName} extension first.`);
};

const getWalletId = (name) => {
    switch (name) {
        case 'MetaMask':
            return 'btn-wallet-metamask';
        case 'WalletConnect':
            return 'btn-wallet-walletconnect';
        case 'Binance Wallet':
            return 'btn-wallet-binance';
        case 'Coinbase Wallet':
            return 'btn-wallet-coinbase';
        default:
            return `btn-wallet-${name.toLowerCase().replace(/\s+/g, '')}`;
    }
};

const WalletConnect = ({ onConnectClick, onDisconnectClick, onConnectCallback }) => {
    const { theme } = useTheme();
    const { t } = useTranslation();
    const location = useLocation();
    const [showWallet, setShowWallet] = useState(false);
    const [showSignModal, setShowSignModal] = useState(false);
    const [loader, setLoader] = useState(false);
    const [isConnected, setIsConnected] = useState(false);
    const [referralCode, setReferralCode] = useState("");
    const [smartAccount, setSmartAccount] = useRecoilState(userSmartAccount);
    const [smartAccountClient, setSmartAccountClient] = useRecoilState(userSmartAccountClient);
    const [publicClient, setPublicClient] = useRecoilState(userPublicClient);
    const [userProfile, setUserProfile] = useRecoilState(userProfileData);
    const { connect } = useConnect();
    const { disconnect, disconnectAsync } = useDisconnect();
    const { address, connector: activeConnector } = useAccount();
    const { data: walletClient } = useWalletClient();
    const { reset: resetSignMessage } = useSignMessage();
    const wallets = useWalletList();
    const connectedChainId = useChainId();
    const switchChain = useSwitchChain();
    const customRPC = getNodeUrl();
    const accessToken = localStorage.getItem("accessToken");
    const getAllBalances = useGetAllBalances();
    const handleCloseWallet = () => setShowWallet(false);
    const handleShowWallet = () => setShowWallet(true);
    const handleCloseSignModal = () => setShowSignModal(false);
    const { grantPermissions } = useSessionPermissions();
    const { getAllowanceByAddress } = useCheckAllowance();

    useEffect(() => {
        const searchParams = new URLSearchParams(window.location.search);
        const codeFromQuery = searchParams.get("code");
        if (codeFromQuery) {
            setReferralCode(codeFromQuery);
        }
    }, []);

    useEffect(() => {
        if (address && smartAccount) {
            const lastConnectedAccount = localStorage.getItem("lastAccount");
            if (lastConnectedAccount !== address) {
                localStorage.removeItem("sessionExpiryTime");
            }
        }
    }, [address, smartAccount]);

    const handleDisconnect = () => {
        resetSignMessage();
        disconnect();
        setSmartAccount(null);
        localStorage.removeItem("connectorId");
        localStorage.removeItem("chain");
        localStorage.removeItem("flag");
        localStorage.removeItem("userId");
        localStorage?.removeItem("accessToken");
        localStorage.removeItem("lastAccount");
        localStorage.removeItem("sign");
        localStorage.removeItem("sessionExpiryTime");
    };

    const connectWalletWagmi = async (wallet) => {
        const { connector: walletConnector, name } = wallet;
        try {
            // On mobile, MetaMask/Binance don't have injected connectors.
            // Use the registered WC connector but suppress its modal and deep-link directly.
            if (isMobile() && !walletConnector && MOBILE_WC_FALLBACK_WALLETS.includes(name)) {
                const wcWallet = wallets?.find(w => w.name === 'WalletConnect');
                if (!wcWallet?.connector) {
                    notifyWarning(`Unable to connect ${name}. Please try WalletConnect.`);
                    return;
                }

                handleCloseWallet();
                setLoader(true);

                const wcConnector = wcWallet.connector;

                // Properly disconnect any existing WC session through wagmi
                // so the provider starts clean without stale session topics
                try { await disconnectAsync({ connector: wcConnector }); } catch (_) { }

                const provider = await wcConnector.getProvider();

                // Temporarily remove the modal so WC's internal handler skips it
                const savedModal = provider.modal;
                provider.modal = null;

                const onDisplayUri = (uri) => {
                    provider.removeListener('display_uri', onDisplayUri);
                    // Restore the modal for future WalletConnect button use
                    provider.modal = savedModal;
                    const deepLink = getWalletDeepLink(name, uri);
                    if (deepLink) {
                        // Dismiss loader when user returns from the wallet app
                        const onPageVisible = () => {
                            if (document.visibilityState === 'visible') {
                                document.removeEventListener('visibilitychange', onPageVisible);
                                clearTimeout(installFallbackTimer);
                                setLoader(false);
                            }
                        };
                        document.addEventListener('visibilitychange', onPageVisible);

                        // If the app isn't installed, the deep link silently fails.
                        // Redirect to the install page if user is still on this page after 2.5s.
                        const installLink = getWalletInstallLink(name);
                        const installFallbackTimer = installLink ? setTimeout(() => {
                            document.removeEventListener('visibilitychange', onPageVisible);
                            setLoader(false);
                            window.location.href = installLink;
                        }, 2500) : null;

                        window.location.href = deepLink;
                    }
                };
                provider.on('display_uri', onDisplayUri);

                connect({ connector: wcConnector });
                localStorage.setItem("connectorId", name);
                localStorage.setItem("flag", "true");
                localStorage.setItem("userlastconnectorId", name);
                setIsConnected(true);
                return;
            }

            handleCloseWallet();

            walletConnector?.name === 'WalletConnect'
                ? connect({ connector: walletConnector })
                : connect({ connector: walletConnector, chainId: platform_chainId });

            localStorage.setItem("connectorId", walletConnector?.name || name);
            localStorage.setItem("flag", "true");
            localStorage.setItem("userlastconnectorId", walletConnector?.name || name);
            setIsConnected(true);
        } catch (error) {
            console.log(error);
            handleCloseWallet();
            setLoader(false);
            localStorage.removeItem("TokendollorVal");
            localStorage.removeItem("connectorId");
            localStorage.removeItem("flag");
            localStorage.removeItem("accessToken");
            localStorage.removeItem("userId");
        }
    };

    const nullApproval = 0n

    const approvalTransaction = async () => {
        const callArgs = [];
        callArgs.push({
            to: usdr_token,
            data: encodeFunctionData({
                abi: BalanceOfAbi,
                functionName: "approve",
                args: [OLD_FACTORY_CONTRACT, nullApproval],
            }),
        });
        callArgs.push({
            to: RAIN_ARBI_CONTRACT_ADDRESS,
            data: encodeFunctionData({
                abi: BalanceOfAbi,
                functionName: "approve",
                args: [OLD_FACTORY_CONTRACT, nullApproval],
            }),
        });

        return callArgs;
    }

    const handleClearApprovals = async () => {
        try {
            const usdtAllowance = await getAllowanceByAddress(usdr_token, OLD_FACTORY_CONTRACT)
            const rainAllowance = await getAllowanceByAddress(RAIN_ARBI_CONTRACT_ADDRESS, OLD_FACTORY_CONTRACT)
            console.log(usdtAllowance)
            console.log(rainAllowance)

            if (usdtAllowance > 0 || rainAllowance > 0) {

                const callArgs = await approvalTransaction();
                const { userPermissions: permissions, userSessionKey: sessionKey } =
                    await grantPermissions();
                const preparedCalls = await smartAccountClient.prepareCalls({
                    calls: callArgs,
                    from: smartAccount,
                    capabilities: { permissions },
                });
                const signedCalls = await signPreparedCalls(sessionKey, preparedCalls);
                const sendResult = await smartAccountClient.sendPreparedCalls({
                    ...signedCalls,
                    capabilities: { permissions },
                });
                const { preparedCallIds } = sendResult;
                // const { txHash } = await getTxHashAndReceipt({
                //     preparedCallId: preparedCallIds[0],
                //     smartAccountClient,
                //     publicClient,
                // });
                if (preparedCallIds) {
                    notifySuccess(SAFE_FUNDS_WARNING)
                }
            }
        } catch (error) {
            console.log('Error clearing approvals: ', error);
        }
    }

    useEffect(() => {
        (async () => {
            if (smartAccount) {
                await handleClearApprovals()
            }
        })()
    }, [smartAccount])

    const signHandleWagmi = async () => {
        handleCloseSignModal();
        let restoreWindowOpen = null;
        try {
            setLoader(true);
            const lastConnectorId = localStorage.getItem("userlastconnectorId");

            if (connectedChainId !== platform_chainId) {
                await switchChain.mutateAsync({ chainId: platform_chainId });
            }

            const isWcConnection = activeConnector?.id === 'walletConnect';
            const needsMobileRedirect = isMobile() && isWcConnection && MOBILE_WC_FALLBACK_WALLETS.includes(lastConnectorId);
            const walletLink = needsMobileRedirect ? getWalletOpenLink(lastConnectorId) : null;

            // Block WC's internal redirect — it uses stale session metadata
            // and may open the wrong wallet (e.g. MetaMask instead of Binance)
            if (needsMobileRedirect) {
                const origOpen = window.open.bind(window);
                window.open = () => null;
                restoreWindowOpen = () => { window.open = origOpen; };
            }

            const signPromise = signMessage(config, {
                connector: activeConnector,
                message: address?.toLowerCase()
            });

            // Redirect to the correct wallet ourselves
            if (walletLink) {
                setTimeout(() => {
                    if (restoreWindowOpen) restoreWindowOpen();
                    restoreWindowOpen = null;
                    window.location.href = walletLink;
                }, 500);
            }

            const signData = await signPromise;
            if (restoreWindowOpen) restoreWindowOpen();

            localStorage.setItem("connectorId", lastConnectorId);
            localStorage.setItem("sign", signData);
            localStorage.setItem("lastAccount", address);

            const newAccount = await getAlchemySmartAccount();
            await handleClearApprovals()
            await LoginWithWallet(signData, address, newAccount);

            return signData;
        } catch (error) {
            if (restoreWindowOpen) restoreWindowOpen();
            setLoader(false);
            notifyError(t("Signature request rejected. Please try again."));
            handleDisconnect();
            console.error("Signing failed:", error);
            return null;
        }
    };

    const LoginWithWallet = async (signature, address, smartWallet) => {
        try {
            const response = await axios.post(
                `${api_url}/auth/login-or-register-with-walletAddress`,
                {
                    sign: signature,
                    walletAddress: address,
                    userSmartAddress: smartWallet,
                    referredBy: referralCode
                },
            );

            notifySuccess(LOGIN_SUCCESS);

            const res = response.data.token;
            const res2 = response.data?.user?._id;
            const connectorID = localStorage.getItem("connectorId");

            localStorage.setItem("accessToken", res);
            localStorage.setItem("userlastconnectorId", connectorID);
            localStorage.setItem("userId", res2);
            localStorage?.setItem("flag", true);
            localStorage.setItem("sign", signature);

            setLoader(false);
        } catch (error) {
            console.error("Registration failed:", error);
            setLoader(false);
            throw error;
        }
    };

    const getAlchemySmartAccount = async () => {
        try {
            const signer = new WalletClientSigner(
                createWalletClient({
                    chain: arbitrum,
                    transport: custom(walletClient)
                }),
                'wallet'
            );

            const client = createSmartWalletClient({
                transport: alchemy({
                    apiKey: ALCHEMY_API_KEY,
                    nodeRpcUrl: customRPC
                }),
                chain: arbitrum,
                signer: signer,
                policyId: PAYMASTER_POLICY_ID,
            });

            const smartAccountAddress = await client?.requestAccount();
            setSmartAccount(smartAccountAddress?.address);
            setSmartAccountClient(client);

            return smartAccountAddress?.address;
        } catch (error) {
            console.log('error: ', error);
        }
    };

    const getPublicClient = async () => {
        try {
            const publicClient = createPublicClient({
                chain: arbitrum,
                transport: http()
            });
            setPublicClient(publicClient);
        } catch (error) {
            console.log('error: ', error);
        }
    };

    useEffect(() => {
        if (smartAccount) {
            getPublicClient();
        }
    }, [smartAccount]);

    useEffect(() => {
        if (address && walletClient) {
            (async () => {
                await getAlchemySmartAccount();
            })();
        } else {
            setSmartAccount(null);
        }
    }, [address, walletClient]);

    useEffect(() => {
        if (smartAccount) {
            (async () => {
                await getAllBalances();
                if (accessToken) {
                    const response = await getUsersProfile();
                    setUserProfile(response?.data?.data);
                }
            })();
        }
    }, [smartAccount, address, accessToken]);

    useEffect(() => {
        const lastAccount = localStorage.getItem("lastAccount");
        const flag = localStorage.getItem("flag")
        const isLastAccountSame = address === lastAccount;
        if (address && !isConnected) {
            setIsConnected(true);
        }
        if (address && isConnected && !isLastAccountSame && flag) {
            setShowSignModal(true);
        }
    }, [address, isConnected]);

    useEffect(() => {
        const onStorageChange = (e) => {
            if (e.key === "sign" && e.newValue) {
                setShowSignModal(false);
            }
        };
        window.addEventListener("storage", onStorageChange);
        return () => window.removeEventListener("storage", onStorageChange);
    }, []);

    useEffect(() => {
        if (onConnectClick) {
            onConnectClick.current = handleShowWallet;
        }
        if (onDisconnectClick) {
            onDisconnectClick.current = handleDisconnect;
        }
        if (onConnectCallback) {
            onConnectCallback(() => handleShowWallet);
        }
    }, []);

    const swipeRef = useSwipeClose(showWallet, handleCloseWallet);

    return (
        <>
            {loader && <Loader />}

            {
                location.pathname === "/liquidity" ? <>
                    {address ? (
                        <a
                            href="#"
                            className="brand__button disconnect-btn"
                            onClick={(e) => {
                                e.preventDefault();
                                handleDisconnect();
                            }}
                        >
                            Disconnect Wallet
                        </a>
                    ) : (
                        <a
                            href="#"
                            className="brand__button"
                            onClick={(e) => {
                                e.preventDefault();
                                handleShowWallet();
                            }}
                        >
                            Connect Wallet
                        </a>
                    )}</> : <>
                    {!address && (
                        window.innerWidth < 991 ? <><a
                            className="btn-connectmobile"
                            onClick={() => {
                                handleShowWallet();
                            }}
                        >
                            Login
                        </a></> : <>  <div class="dropdown connect-btn">
                            <button
                                id="btn-login"
                                class="dropdown-toggle"
                                type="button"
                                data-bs-toggle="dropdown"
                                aria-expanded="false"
                            >
                                {t("Login")}
                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                                    <path d="M6.69047 12.122L2.75297 8.18453C2.67088 8.10244 2.62476 7.9911 2.62476 7.875C2.62476 7.7589 2.67088 7.64756 2.75297 7.56547C2.83506 7.48337 2.9464 7.43726 3.0625 7.43726C3.1786 7.43726 3.28994 7.48337 3.37203 7.56547L6.5625 10.7565L6.5625 2.1875C6.5625 2.07147 6.60859 1.96019 6.69064 1.87814C6.77269 1.79609 6.88397 1.75 7 1.75C7.11603 1.75 7.22731 1.79609 7.30936 1.87814C7.39141 1.96019 7.4375 2.07147 7.4375 2.1875L7.4375 10.7565L10.628 7.56547C10.7101 7.48338 10.8214 7.43726 10.9375 7.43726C11.0536 7.43726 11.1649 7.48338 11.247 7.56547C11.3291 7.64756 11.3752 7.7589 11.3752 7.875C11.3752 7.9911 11.3291 8.10244 11.247 8.18453L7.30953 12.122C7.2689 12.1627 7.22065 12.195 7.16754 12.217C7.11443 12.239 7.05749 12.2503 7 12.2503C6.94251 12.2503 6.88558 12.239 6.83246 12.217C6.77935 12.195 6.7311 12.1627 6.69047 12.122Z" fill="black" />
                                </svg>
                            </button>
                            <ul class="dropdown-menu">
                                <h6 className="main-head">{t("Connect a wallet")}</h6>
                                <div className="parent">
                                    <div className="upper-text">
                                        <h6>
                                            {t("Rain operates on")}
                                            {
                                                theme === DARK_THEME ? <img
                                                    src="\assets\arbitrum.svg"
                                                    alt="img"
                                                    className="img-fluid"
                                                /> : <img
                                                    src="\assets\arblogolight.svg"
                                                    alt="img"
                                                    className="img-fluid"
                                                />
                                            }
                                        </h6>
                                        <p>
                                            {t(
                                                "To participate, ensure your wallet is connected to Arbitrum."
                                            )}
                                        </p>
                                    </div>

                                    <div className="twice-btns">
                                        {wallets?.map((wallet) => (
                                            <button
                                                id={getWalletId(wallet.name)}
                                                key={wallet.name}
                                                onClick={() => {
                                                    if (wallet?.isAvailable) {
                                                        connectWalletWagmi(wallet)
                                                    } else {
                                                        handleWalletNotInstalled(wallet.name);
                                                    }
                                                }}
                                                type="button"
                                                className={!wallet.isAvailable ? 'disabled-wallet' : ''}
                                            >
                                                {getWalletImage(wallet.name) && (
                                                    <img
                                                        src={getWalletImage(wallet.name)}
                                                        alt={wallet.name}
                                                        className="wallet-icon"
                                                    />
                                                )}
                                                {wallet?.name}

                                            </button>
                                        ))}
                                    </div>
                                    <p className="bottom-info">
                                        {t(
                                            "By connecting a wallet, you agree to Rain's Terms of Service and consent to its Privacy Policy."
                                        )}
                                    </p>
                                </div>
                            </ul>
                        </div></>
                    )}
                </>
            }

            <Modal
                className="common-modal-style wallet-modal new-modal-style"
                show={showWallet}
                onHide={handleCloseWallet}
                centered
                animation={false}
            >
                {
                    window.innerWidth > 991 ? "" : <Modal.Header className='m-0' ref={swipeRef} onClick={handleCloseWallet}>
                        <div className="close-div d-none"></div>
                    </Modal.Header>
                }
                <Modal.Body>
                    <div className="parent" style={{paddingTop: "12px"}}>
                        <div className="main-img">
                            <svg xmlns="http://www.w3.org/2000/svg" width="34" height="34" viewBox="0 0 34 34" fill="none">
                                <path d="M24.082 19.8335H24.0954" stroke="#212121" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" />
                                <path d="M9.91667 9.91667H26.9167C27.6681 9.91667 28.3888 10.2152 28.9201 10.7465C29.4515 11.2779 29.75 11.9986 29.75 12.75V26.9167C29.75 27.6681 29.4515 28.3888 28.9201 28.9201C28.3888 29.4515 27.6681 29.75 26.9167 29.75H7.08333C6.33189 29.75 5.61122 29.4515 5.07986 28.9201C4.54851 28.3888 4.25 27.6681 4.25 26.9167V7.08333C4.25 6.33189 4.54851 5.61122 5.07986 5.07986C5.61122 4.54851 6.33189 4.25 7.08333 4.25H26.9167" stroke="#212121" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" />
                            </svg>
                        </div>
                        <div className="upper-text">
                            <h6>
                                {t("Rain operates on")}
                                {theme === DARK_THEME ? (
                                    <img
                                        src="\assets\arbitrum.svg"
                                        alt="img"
                                        className="img-fluid"
                                        style={{marginTop: "4px"}}
                                    />
                                ) : (
                                    <img
                                        src="\assets\arblogolight.svg"
                                        alt="img"
                                        className="img-fluid"
                                        style={{marginTop: "4px"}}
                                    />
                                )}
                            </h6>
                            <p>
                                {t("Connect you wallet to start trading and interact with this market")}
                            </p>
                        </div>
                        <div className="twice-btns twice-btns-wallet-mobile" style={{ display: "grid", gridTemplateColumns: "1fr" }}>
                            {wallets?.map((wallet) => (
                                <button
                                    id={getWalletId(wallet.name)}
                                    key={wallet.name}
                                    onClick={() => {
                                        if (wallet?.isAvailable) {
                                            connectWalletWagmi(wallet);
                                        } else {
                                            handleWalletNotInstalled(wallet.name);
                                        }
                                    }}
                                    type="button"
                                    className={!wallet.isAvailable ? 'disabled-wallet mb-2' : 'mb-2'}
                                >
                                    {getWalletImage(wallet.name) && (
                                        <img
                                            src={getWalletImage(wallet.name)}
                                            alt={wallet.name}
                                            className="wallet-icon"
                                        />
                                    )}
                                    {wallet?.name}
                                </button>
                            ))}
                        </div>
                        <p className="bottom-info">
                            {t("By connecting a wallet, you agree to Rain's Terms of Service and consent to its Privacy Policy.")}
                        </p>
                    </div>
                </Modal.Body>
            </Modal>

            <Modal
                className="create importexisting-modal"
                show={showSignModal}
                onHide={handleCloseSignModal}
                backdrop="static"
                keyboard={false}
                centered
            >
                <Modal.Body>
                    <div className="main-content">
                        <div className="main-icon">
                            <svg xmlns="http://www.w3.org/2000/svg" width="37" height="37" viewBox="0 0 37 37" fill="none">
                                <path d="M26.2083 21.5833H26.2228" stroke="#DAD9D9" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M10.7917 10.7917H29.2917C30.1094 10.7917 30.8937 11.1165 31.4719 11.6948C32.0501 12.273 32.375 13.0572 32.375 13.875V29.2917C32.375 30.1094 32.0501 30.8937 31.4719 31.4719C30.8937 32.0501 30.1094 32.375 29.2917 32.375H7.70833C6.89058 32.375 6.10632 32.0501 5.52809 31.4719C4.94985 30.8937 4.625 30.1094 4.625 29.2917V7.70833C4.625 6.89058 4.94985 6.10632 5.52809 5.52809C6.10632 4.94985 6.89058 4.625 7.70833 4.625H29.2917" stroke="#DAD9D9" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </div>
                        <h5>{t('Sign to Proceed')}</h5>
                        <p style={{ color: "#737373" }}>
                            {t('This signature confirms your identity and connects your wallet safely to the platform.')}
                        </p>
                    </div>
                    <div className="twice-btnss" style={{ gridTemplateColumns: "1fr" }}>
                        <button
                            id="btn-sign"
                            className="btn-common"
                            onClick={() => signHandleWagmi()}
                        >
                            {t("Sign")}
                        </button>
                    </div>
                </Modal.Body>
            </Modal>
        </>
    );
};

export default WalletConnect;